'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address } from '@morpho-org/blue-sdk';
import type { Time } from '@morpho-org/morpho-ts';
import { parseApiAsset, useCachedValue } from '@morpho-org/dapp-core';
import { Select } from '@morpho-org/ui-v6/client';
import { Skeleton } from '@morpho-org/ui-v6/server';
import { cn, formatters } from '@morpho-org/ui-v6/utils';
import { SuspenseErrorBoundary } from '@repo/ui';
import { useQueryErrorResetBoundary, useSuspenseQuery } from '@tanstack/react-query';
import { useState, useTransition } from 'react';
import type { HistoryRange } from '@/lib/transforms/history/window';
import SimpleChart from '@/components/charts/components/SimpleChart';
import { createPlotlineLabelFormatter, formatTimeseriesData } from '@/components/charts/helpers/highcharts';
import { useHighchartsOptions } from '@/components/charts/hooks/useHighchartsOptions';
import { ErrorDataUnavailable } from '@/components/common/Error';
import { IconRewardHover } from '@/components/common/IconRewardHover';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/common/overlays';
import MerklRewardsHoverCard from '@/components/common/RewardsHoverCard/MerklRewardsHoverCard';
import { timeFrameOptions } from '@/config/table';
import { normalizeVaultApyRewards } from '@/helpers/rewards';
import { useVaultV1Apy } from '@/hooks/useVaultV1Apy';
import { DEFAULT_APY_HISTORY_TIMEFRAME, toHistoryRange } from '@/lib/transforms/history/toHistoryRange';
import type { VaultApyCardData } from '../VaultApyCard.mapper';
import { apyHistoryQueryOptions } from './apyHistoryQuery';
const CHART_HEIGHT = '225px';
export interface ChartApyCardProps {
  chainId: number;
  address: Address;
  vault: VaultApyCardData;
}

/**
 * Chart component to display the vault apy during the time
 */
const ChartApyCardClient = t0 => {
  const $ = _c(64);
  const {
    chainId,
    address,
    vault
  } = t0;
  const [timeFrame, setTimeFrame] = useState(DEFAULT_APY_HISTORY_TIMEFRAME);
  const [isRangePending, startTransition] = useTransition();
  let t1;
  if ($[0] !== timeFrame) {
    t1 = toHistoryRange(timeFrame);
    $[0] = timeFrame;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const range = t1;
  const {
    reset: resetQueryErrors
  } = useQueryErrorResetBoundary();
  const t2 = vault.address;
  const t3 = vault.asset;
  const t4 = vault.state;
  let t5;
  if ($[2] !== vault.rewards) {
    t5 = normalizeVaultApyRewards(vault.rewards);
    $[2] = vault.rewards;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== t5 || $[5] !== vault.address || $[6] !== vault.asset || $[7] !== vault.state) {
    t6 = {
      vaultV1: {
        address: t2,
        asset: t3,
        state: t4,
        rewards: t5
      }
    };
    $[4] = t5;
    $[5] = vault.address;
    $[6] = vault.asset;
    $[7] = vault.state;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const {
    apy,
    netApy: rawNetApy,
    performanceFee,
    points,
    rewards
  } = useVaultV1Apy(t6);
  const netApy = useCachedValue(rawNetApy);
  let t7;
  if ($[9] !== netApy) {
    t7 = formatters.percent.default("-").digits(2).max(10).of(netApy, 18);
    $[9] = netApy;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const formattedNetApy = t7;
  let t8;
  if ($[11] !== vault.asset) {
    t8 = parseApiAsset(vault.asset);
    $[11] = vault.asset;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== apy || $[14] !== netApy || $[15] !== performanceFee || $[16] !== points || $[17] !== rewards || $[18] !== t8) {
    t9 = {
      performanceFee,
      apy,
      rewards,
      points,
      variant: "vault" as const,
      netApy,
      asset: t8
    };
    $[13] = apy;
    $[14] = netApy;
    $[15] = performanceFee;
    $[16] = points;
    $[17] = rewards;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const rewardsHoverCardProps = t9;
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="text-secondary-foreground text-xs leading-normal">Net APY</span>;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== rewardsHoverCardProps) {
    t11 = <IconRewardHover RewardsHoverCardProps={rewardsHoverCardProps} noHover={true}>{t10}</IconRewardHover>;
    $[21] = rewardsHoverCardProps;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== formattedNetApy) {
    t12 = <span data-testid="vault-v1-apy-card-net-apy" className="text-title-sm text-foreground">{formattedNetApy}</span>;
    $[23] = formattedNetApy;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="text-title-sm text-muted-foreground">%</span>;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t12) {
    t14 = <HoverCardTrigger asChild={true}><div className="flex items-center gap-[2px]">{t12}{t13}</div></HoverCardTrigger>;
    $[26] = t12;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      minWidth: "275px",
      maxWidth: "315px"
    };
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== rewardsHoverCardProps) {
    t16 = <HoverCardContent side="top" sideOffset={8} onClick={_temp} style={t15}><MerklRewardsHoverCard {...rewardsHoverCardProps} /></HoverCardContent>;
    $[29] = rewardsHoverCardProps;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t14 || $[32] !== t16) {
    t17 = <HoverCard openDelay={200} closeDelay={100}>{t14}{t16}</HoverCard>;
    $[31] = t14;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t11 || $[35] !== t17) {
    t18 = <div className="gap-xs flex flex-col">{t11}{t17}</div>;
    $[34] = t11;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== resetQueryErrors) {
    t19 = value => {
      resetQueryErrors();
      startTransition(() => setTimeFrame(value));
    };
    $[37] = resetQueryErrors;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t19 || $[40] !== timeFrame) {
    t20 = <Select options={timeFrameOptions} value={timeFrame} onChange={t19} />;
    $[39] = t19;
    $[40] = timeFrame;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t18 || $[43] !== t20) {
    t21 = <div className="flex items-center justify-between">{t18}{t20}</div>;
    $[42] = t18;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  const t22 = isRangePending && "opacity-50";
  let t23;
  if ($[45] !== t22) {
    t23 = cn("transition-opacity", t22);
    $[45] = t22;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== range) {
    t24 = [range];
    $[47] = range;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Skeleton className="w-full rounded-[20px]" style={{
      height: CHART_HEIGHT
    }} />;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <div data-testid="vault-v1-apy-chart-error"><ErrorDataUnavailable minHeight={CHART_HEIGHT} showActions={false} /></div>;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] !== address || $[52] !== chainId || $[53] !== range) {
    t27 = <ApyHistoryChart key={range} chainId={chainId} address={address} range={range} />;
    $[51] = address;
    $[52] = chainId;
    $[53] = range;
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  let t28;
  if ($[55] !== t24 || $[56] !== t27) {
    t28 = <SuspenseErrorBoundary resetKeys={t24} fallback={t25} errorFallback={t26}>{t27}</SuspenseErrorBoundary>;
    $[55] = t24;
    $[56] = t27;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] !== t23 || $[59] !== t28) {
    t29 = <div className={t23}>{t28}</div>;
    $[58] = t23;
    $[59] = t28;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  let t30;
  if ($[61] !== t21 || $[62] !== t29) {
    t30 = <div className="gap-lg flex min-w-[220px] grow flex-col">{t21}{t29}</div>;
    $[61] = t21;
    $[62] = t29;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  return t30;
};
interface ApyHistoryChartProps {
  chainId: number;
  address: Address;
  range: HistoryRange;
}
const ApyHistoryChart = t0 => {
  const $ = _c(23);
  const {
    chainId,
    address,
    range
  } = t0;
  const {
    palette
  } = useHighchartsOptions();
  let t1;
  if ($[0] !== address || $[1] !== chainId || $[2] !== range) {
    t1 = apyHistoryQueryOptions({
      chainId,
      address,
      range
    });
    $[0] = address;
    $[1] = chainId;
    $[2] = range;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    data
  } = useSuspenseQuery(t1);
  let t2;
  if ($[4] !== data.points) {
    t2 = formatTimeseriesData(data.points.map(_temp2));
    $[4] = data.points;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const formattedData = t2;
  let t3;
  if ($[6] !== data.averageNetApy) {
    t3 = formatters.percent.default("-").trailingZero(false).digits(2).of(data.averageNetApy);
    $[6] = data.averageNetApy;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const formattedAvgApy = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      loading: false
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== formattedData || $[10] !== palette.areaChartColors[0] || $[11] !== palette.colors[0]) {
    t5 = [{
      type: "areaspline",
      name: "Apy",
      data: formattedData,
      color: palette.colors[0],
      lineColor: palette.colors[0],
      fillColor: palette.areaChartColors[0]
    }];
    $[9] = formattedData;
    $[10] = palette.areaChartColors[0];
    $[11] = palette.colors[0];
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = data.averageNetApy ?? undefined;
  const t7 = `Avg ${formattedAvgApy}%`;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = createPlotlineLabelFormatter("var(--foreground)");
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t7) {
    t9 = {
      x: 6,
      y: -15,
      text: t7,
      align: "left",
      useHTML: true,
      formatter: t8
    };
    $[14] = t7;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t6 || $[17] !== t9) {
    t10 = [{
      value: t6,
      className: "hc-plotline-brand",
      color: "var(--primary)",
      dashStyle: "Dot",
      width: 1,
      label: t9
    }];
    $[16] = t6;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      yAxis: {
        softMin: 0,
        maxPadding: 0.4
      },
      chart: {
        plotBackgroundColor: "var(--muted)"
      }
    };
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t10 || $[21] !== t5) {
    t12 = <div data-testid="vault-v1-apy-chart"><SimpleChart statuses={t4} unit="%" height={CHART_HEIGHT} series={t5} plotlinesY={t10} additionalOptions={t11} /></div>;
    $[20] = t10;
    $[21] = t5;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  return t12;
};
export default ChartApyCardClient;
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(point) {
  return {
    x: point.timestamp,
    y: point.netApy
  };
}